import React, { useState, useEffect } from 'react';
import { auth } from '../api';

const ResendVerificationPage = ({ setCurrentPage }) => {
  const [userEmail, setUserEmail] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [status, setStatus] = useState('');
  const [error, setError] = useState('');

  // Get the current user's email on component mount
  useEffect(() => {
    const fetchUserProfile = async () => {
      try {
        const response = await auth.getCurrentUser();
        if (response.data && response.data.email) {
          setUserEmail(response.data.email);
        } else {
          setError('Unable to retrieve your email address. Please try again later.');
        }
      } catch (err) {
        console.error('Error fetching user profile:', err);
        setError('You must be logged in to resend a verification email.');
        setTimeout(() => {
          setCurrentPage('login');
        }, 3000);
      } finally {
        setLoading(false);
      }
    };

    fetchUserProfile();
  }, [setCurrentPage]);

  const handleResendVerification = async () => {
    if (!userEmail) {
      setError('Email address is not available.');
      return;
    }

    setSending(true);
    setError('');
    setStatus('');

    try {
      await auth.resendVerification(userEmail);
      setStatus('success');
    } catch (err) {
      console.error('Error resending verification:', err);
      setError(err.response?.data?.detail || 'Failed to resend verification email. Please try again later.');
      setStatus('error');
    } finally {
      setSending(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex flex-col justify-center items-center py-12 bg-gray-50">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-emerald-500"></div>
        <p className="mt-4 text-gray-600">Loading...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col justify-center py-12 sm:px-6 lg:px-8 bg-gray-50">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
          Resend Verification Email
        </h2>
        <p className="mt-2 text-center text-sm text-gray-600">
          We'll send a new verification link to your registered email address
        </p>
      </div>

      <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
        <div className="bg-white py-8 px-4 shadow sm:rounded-lg sm:px-10">
          {status === 'success' ? (
            <div className="text-center">
              <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-green-100">
                <svg className="h-6 w-6 text-green-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <h3 className="mt-3 text-lg font-medium text-gray-900">Verification email sent!</h3>
              <p className="mt-2 text-sm text-gray-500">
                We've sent a verification link to <span className="font-medium">{userEmail}</span>. 
                Please check your inbox and click the link to verify your email address.
              </p>
              <div className="mt-6">
                <button
                  onClick={() => setCurrentPage('home')}
                  className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-emerald-600 hover:bg-emerald-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-emerald-500"
                >
                  Return to Home
                </button>
              </div>
            </div>
          ) : (
            <div className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Your Email Address
                </label>
                <div className="mt-1">
                  <div className="flex items-center p-2 bg-gray-50 border border-gray-300 rounded-md">
                    <span className="block w-full text-gray-700">{userEmail}</span>
                  </div>
                  <p className="mt-1 text-xs text-gray-500">
                    The verification link will be sent to this email address
                  </p>
                </div>
              </div>

              {error && (
                <div className="text-sm text-red-600 bg-red-50 p-2 rounded">{error}</div>
              )}

              <div>
                <button
                  type="button"
                  onClick={handleResendVerification}
                  disabled={sending || !userEmail}
                  className={`w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-emerald-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-emerald-500 ${
                    (sending || !userEmail) ? 'opacity-70 cursor-not-allowed' : 'hover:bg-emerald-700'
                  }`}
                >
                  {sending ? 'Sending...' : 'Send Verification Link'}
                </button>
              </div>

              <div className="text-sm text-center">
                <button
                  type="button"
                  onClick={() => setCurrentPage('home')}
                  className="font-medium text-emerald-600 hover:text-emerald-500"
                >
                  Back to Home
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ResendVerificationPage;
